import React, { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { trackEvent } from '../lib/analytics';

const Spline = React.lazy(() => import('@splinetool/react-spline'));

const Hero = () => {
  const { t, i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleScrollToServices = () => {
    const servicesSection = document.querySelector('#services');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Split headline to highlight key words
  const highlightWords = ['Digital', 'Excellence', 'Digitale', 'Excelencia', 'الرقمي', 'التميز'];

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-black" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Grid Pattern Background */}
      <div className="absolute inset-0 grid-pattern opacity-50" />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-black/95 to-black/80" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-5 md:px-[7.6923%] pt-32 pb-20">
        <div className={`flex flex-col lg:flex-row items-center gap-12 lg:gap-0 ${isRTL ? 'lg:flex-row-reverse' : ''}`}>
          {/* Left Content */}
          <div className="flex-1 max-w-2xl">
            {/* Eyebrow Text */}
            <div
              className={`inline-flex items-center gap-2 mb-6 opacity-0 ${
                isVisible ? 'animate-fade-in-up' : ''
              }`}
            >
              <span className="w-2 h-2 bg-[#00FFD1] animate-pulse" />
              <span className="text-[#00FFD1] text-sm font-medium tracking-wider uppercase">
                {t('hero.badge')}
              </span>
            </div>

            {/* Main Headline */}
            <h1
              className={`display-huge text-white mb-6 opacity-0 ${
                isVisible ? 'animate-fade-in-up delay-100' : ''
              }`}
            >
              {t('hero.headline').split(' ').map((word, index) => (
                <span key={`${word}-${index}`} className={highlightWords.some(hw => word.includes(hw)) ? 'text-[#00FFD1]' : ''}>
                  {word}{' '}
                </span>
              ))}
            </h1>

            {/* Subheadline */}
            <p
              className={`body-muted mb-4 opacity-0 ${
                isVisible ? 'animate-fade-in-up delay-200' : ''
              }`}
            >
              {t('hero.subheadline')}
            </p>

            {/* Description */}
            <p
              className={`body-medium text-white/70 mb-10 max-w-xl opacity-0 ${
                isVisible ? 'animate-fade-in-up delay-300' : ''
              }`}
            >
              {t('hero.description')}
            </p>

            {/* CTA Buttons */}
            <div
              className={`flex flex-col sm:flex-row gap-4 opacity-0 ${
                isVisible ? 'animate-fade-in-up delay-400' : ''
              }`}
            >
              <a href="#contact" className="btn-primary group" onClick={() => trackEvent('book_call_click', { location: 'hero' })}>
                {t('hero.primaryCta')}
                <ArrowRight className={`w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 ${isRTL ? 'rotate-180 group-hover:-translate-x-1' : ''}`} />
              </a>
              <button onClick={handleScrollToServices} className="btn-secondary group">
                {t('hero.secondaryCta')}
                <ChevronDown className="w-5 h-5 transition-transform duration-300 group-hover:translate-y-1" />
              </button>
            </div>

            {/* Trust Indicators */}
            <div
              className={`mt-16 pt-8 border-t border-white/10 opacity-0 ${
                isVisible ? 'animate-fade-in-up delay-500' : ''
              }`}
            >
              <p className="text-white/40 text-sm mb-4">{t('hero.trustedBy')}</p>
              <div className="flex items-center gap-8">
                {t('hero.trustLabels', { returnObjects: true }).map((text, index) => (
                  <span
                    key={text}
                    className="text-white/20 text-sm font-medium hover:text-white/50 transition-colors cursor-default"
                  >
                    {text}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right - Spline 3D (pointer-events disabled on mobile/tablet so it never blocks touch-scroll on iOS) */}
          <div className="flex-1 w-full lg:w-auto relative">
            <div 
              className={`w-full h-[500px] lg:h-[700px] overflow-visible relative pointer-events-none lg:pointer-events-auto opacity-0 ${
                isVisible ? 'animate-fade-in delay-300' : ''
              }`}
            >
              <Suspense
                fallback={
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="w-16 h-16 border-2 border-[#00FFD1] border-t-transparent rounded-full animate-spin" />
                  </div>
                }
              >
                <Spline scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode" />
              </Suspense>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown className="w-6 h-6 text-white/30" />
      </div>
    </section>
  );
};

export default Hero;
